import { post } from "@src/appV2/api/parsedApi";
import { getCognitoIdToken } from "@src/appV2/Auth/cognito/cognitoAuth";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

const requestSchema = z.object({
  user_token: z.string(),
});

const responseSchema = z.object({
  jwt: z.string(),
});

type WorkplaceZendeskSsoToken = z.infer<typeof responseSchema>;

/**
 * Mints a short-lived Zendesk SSO JWT for the current user via the
 * zendesk-jwt-service. Zendesk rejects a JWT whose `iat` is more than two
 * minutes old, so this is a mutation (minted just-in-time at launch) rather
 * than a cached query — never persist or reuse the result.
 */
export function useWorkplaceZendeskSsoToken() {
  return useMutation({
    mutationFn: async (): Promise<WorkplaceZendeskSsoToken> => {
      const idToken = await getCognitoIdToken();
      if (!idToken) {
        throw new Error("Cannot mint Zendesk SSO token: user is not authenticated");
      }

      const response = await post({
        url: `${environmentConfig.REACT_APP_ZENDESK_JWT_MICROSERVICE_BASE_URL}/zendesk/jwt`,
        data: { user_token: `Bearer ${idToken}` },
        bodySchema: requestSchema,
        responseSchema,
      });

      return response.data;
    },
  });
}
